// Tier 1 — the first five minutes, one per role. These are the three original tours (2026-09-23) re-cut to a task path
// (TUTORIALS-PLAN.md §8 step 2). Their ids are unchanged on purpose: `vttmaps.tour.gm.done` and friends already live in
// people's browsers, and renaming an id would tour an existing user a second time.
import type { Tutorial } from "./types";

export const GM_FIRST: Tutorial = {
  id: "gm", tier: 1, role: "gm", title: "Your first table", minutes: 3, covers: [1, 2, 3, 13, 14, 23, 9],
  blurb: "Start a campaign, put a map on the table, add a creature, invite your players, roll.",
  autoStart: "welcome",
  steps: [
    {
      key: "g-welcome", surface: "welcome", goto: "/",
      title: "Welcome, Game Master",
      body: "This is your front door. Campaigns you run and campaigns you play in both live here.\n\nLet's set up your first game. It takes about three minutes, and you can Skip any time — or Later, and I'll offer once more next time.",
    },
    {
      key: "g-start", surface: "welcome", anchor: "gm-start", placement: "bottom", advanceOnClick: true,
      title: "Start a campaign",
      body: "Tap “Start a new campaign.” The only thing you must fill in is a name — everything else can wait.\n\nPrefer to look around a finished one first? “Start with the sample campaign” beside it builds a small practice game you can delete later.",
    },
    {
      key: "g-name", surface: "new-campaign", anchor: "nc-name", placement: "bottom", goto: "/new-campaign", skipIfMissing: true,
      title: "Name it",
      body: "Give the campaign a name. Optionally roll a starting point — it drafts your notes and shelves a town for you.\n\nThen press “Start this campaign” and you'll land in your workspace.",
    },
    {
      key: "g-home", surface: "workspace", anchor: "ws-home", placement: "bottom", skipIfMissing: true,
      title: "Your campaign home",
      body: "This is the campaign's home page: your party, your notes, your trackers and your sessions. From here you build out the world.\n\nOpen the map screen (the map button in the top bar) and I'll point out the rest.",
    },
    {
      // Not skip-if-missing: it waits, centred, until you reach the map screen, then jumps to the rail.
      key: "g-rail", surface: "workspace", anchor: "ws-rail", placement: "right",
      title: "The right rail",
      body: "On the map screen, everything you build lives in this rail — three groups: Live, Assets, Maps. Most tools work by “arm it, then click the map”: tap the tool, then tap where it goes.",
    },
    {
      key: "g-map", surface: "workspace", anchor: "rail-add", open: { section: "add", tab: "upload" }, placement: "right",
      title: "Add your first map",
      body: "The Maps → Add card is where a battle map comes in — drop an image, sketch one, or build one from pieces. The dropzone is always open; there's no “upload” button to hunt for.\n\nThen Calibrate sets the grid and Save keeps it — one step that unlocks tokens, fog and rooms.",
    },
    {
      key: "g-creature", surface: "workspace", anchor: "rail-creatures", open: { section: "creatures", tab: "bestiary" }, placement: "right",
      title: "Add a creature",
      body: "Cards → From the book searches the Codex's whole bestiary — more than 7,600 creatures. Pick one, say how many, and “Put on map” — or “Add to fight” to start a fight in one click.",
    },
    {
      key: "g-share", surface: "workspace", anchor: "ws-share", placement: "bottom",
      title: "Show the players",
      body: "“Players see it” puts this map on the players' table; Player view shows exactly what they see. Invite players from Campaign settings, and roll the header die with “Party” so everyone sees it land.\n\nThat's the first five minutes. Everything else is under your account menu → Tutorials.",
    },
  ],
};

export const PLAYER_FIRST: Tutorial = {
  id: "player", tier: 1, role: "player", title: "Your seat", minutes: 3, covers: [23, 24, 22],
  blurb: "Your character, your sheet, what the GM shares, and the chat.",
  autoStart: "play", phone: false,
  steps: [
    {
      key: "p-welcome", surface: "play", goto: "/play",
      title: "Welcome to your table",
      body: "This is your home base for the campaign — your character, what the GM shares, and the table chat all live here.\n\nI'll point out the few things that aren't obvious. You can leave any time with Skip.",
    },
    {
      key: "p-character", surface: "play", anchor: "player-character", placement: "bottom",
      title: "This is your character",
      body: "Your character card sits here. Open the full sheet to see everything and to roll.\n\nNo character yet? Build one and send it to your GM — they'll accept it and it becomes yours.",
    },
    {
      key: "p-open-sheet", surface: "play", anchor: "player-open-sheet", placement: "bottom", advanceOnClick: true,
      title: "Open your sheet",
      body: "Tap “Open your sheet” to see it full-size. Go ahead — I'll meet you there.",
    },
    {
      key: "s-roll", surface: "sheet", anchor: "sheet-roll", placement: "bottom",
      title: "Tap anything to roll it",
      body: "This is the one thing to remember: your sheet is a dice tray. Tap a skill, a save, an ability, or an attack and it rolls — big enough for the whole table to read.\n\nNo picking dice, no math. Just tap.",
    },
    {
      key: "s-hp", surface: "sheet", anchor: "sheet-hp", placement: "bottom", skipIfMissing: true,
      title: "Your health and conditions",
      body: "Track hit points and conditions here. When the GM deals damage or you take a hit on the map, it shows up on your sheet automatically.",
    },
    {
      key: "p-shared", surface: "play", anchor: "player-shared", placement: "top", goto: "/play", skipIfMissing: true,
      title: "What the GM shares",
      body: "Handouts, maps, downloads, and any clocks or standings the GM makes visible appear here. This is where the party's shared purse and stash live too.",
    },
    {
      key: "p-chat", surface: "play", anchor: "player-chat", placement: "left", skipIfMissing: true,
      title: "Talk to the table",
      body: "Chat with the whole table, or send a private message to the GM or another player. The pill shows a badge when there's something new. Your Journal, for notes only you can read, sits beside Chat on the map.",
    },
    {
      key: "p-account", surface: "play", anchor: "player-account", placement: "left",
      title: "You're all set",
      body: "That's the tour. Under your account you can set the text size and colours, send feedback, and open Tutorials any time.\n\nThe rest — placing your token, taking your turn — I'll show you the first time you're on the map.",
    },
  ],
};

/** The same seat, on a phone: the tab bar instead of the header, and the gestures that replace a mouse. */
export const PHONE_FIRST: Tutorial = {
  id: "phone", tier: 1, role: "player", title: "Your seat, on a phone", minutes: 3, covers: [11, 23, 24],
  blurb: "The tab bar, the pinned hit points, long-press for help, and tap-to-target.",
  autoStart: "play", phone: true,
  steps: [
    {
      key: "ph-welcome", surface: "play", goto: "/play",
      title: "Welcome to your table",
      body: "On a phone the campaign is an app: Sheet, Map, Stash, Chat and More along the bottom. Add it to your home screen from the browser menu and it opens full-screen.",
    },
    {
      key: "ph-character", surface: "play", anchor: "player-character", placement: "top",
      title: "This is your character",
      body: "Your character card. “Open your sheet” is the big button — the sheet is where you'll spend the session.",
    },
    {
      key: "ph-open-sheet", surface: "play", anchor: "player-open-sheet", placement: "top", advanceOnClick: true,
      title: "Open your sheet",
      body: "Tap it — I'll meet you there.",
    },
    {
      key: "ph-roll", surface: "sheet", anchor: "sheet-roll", placement: "top",
      title: "Tap to roll",
      body: "Every number on the sheet is a die: tap a skill, a save or an attack and it rolls. The last roll stays in a pill at the bottom; tap the pill to see the dice.\n\nLong-press any chip to read what it is.",
    },
    {
      key: "ph-hp", surface: "sheet", anchor: "sheet-hp", placement: "top", skipIfMissing: true,
      title: "Hit points stay pinned",
      body: "Your hit points ride along the top as you scroll, and fold away when you don't need them. Damage from the map lands here by itself.",
    },
    {
      key: "ph-shared", surface: "play", anchor: "player-shared", placement: "top", goto: "/play", skipIfMissing: true,
      title: "What the GM shares",
      body: "Handouts, pictures and the party's stash are under Shared. Chat is its own tab — it won't grab your keyboard until you tap into it. Your Journal is under More on the map, or the Journal row on your home page.",
    },
    {
      key: "ph-account", surface: "play", anchor: "player-account", placement: "bottom",
      title: "You're all set",
      body: "On the map, tap your attack then tap the target, no dragging needed. Press and hold an empty spot to ping the table. Tutorials and your settings are under your account.",
    },
  ],
};

export const TABLE_FIRST: Tutorial = {
  id: "table", tier: 1, role: "player", title: "At the table", minutes: 2, covers: [10, 8, 9],
  blurb: "The live map: place yourself, move, take your turn.",
  autoStart: "table",
  steps: [
    {
      key: "t-welcome", surface: "table",
      title: "You're at the table",
      body: "This is the live map. You see it through your character's eyes — it fills in as you explore, so an empty-looking map just means you haven't been there yet.",
    },
    {
      key: "t-placeme", surface: "table", anchor: "table-placeme", placement: "top",
      title: "Put yourself on the map",
      body: "Tap “Place me,” then tap the square you're standing on. After that you can drag your token to move. “Find me” snaps the view back to you if you get lost.",
    },
    {
      key: "t-attack", surface: "table", anchor: "rail-mysheet", placement: "top", skipIfMissing: true,
      title: "Taking your turn",
      body: "When it's your turn you'll get a “Your turn” nudge. To attack, drag an attack from your play sheet onto the target's token — on a phone, tap the attack, then the target. The roll and damage happen for you.",
    },
    {
      // The ping (feature 08, phase 1; lib/ping.ts) — before t-rolls, so "That's everything" below stays true.
      key: "t-ping", surface: "table", anchor: "table-board", placement: "top", skipIfMissing: true,
      title: "Show where you mean",
      body: "Press and hold on an empty spot of the map for half a second and a ring shows for everyone for three seconds. Handy when you mean this square, not that one.",
    },
    {
      key: "t-rolls", surface: "table", anchor: "table-rolls", placement: "right", skipIfMissing: true,
      title: "Everyone's rolls",
      body: "Every roll at the table lands here, large and readable, so the whole room can follow along. That's everything — enjoy the game.",
    },
  ],
};

export const TIER1: Tutorial[] = [GM_FIRST, PLAYER_FIRST, PHONE_FIRST, TABLE_FIRST];
